import { ChangeEvent, DragEvent, FC, useState } from 'react'

import { CloudArrowDownLineIcon, IconSizes } from '@inno-ui/icons'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import type { LogExport } from 'shared/services/logger/logger.types'

import { parseLogExport } from '../logExportSchema'
import styles from './LogDropzone.module.scss'

const MAX_IMPORT_BYTES = 25 * 1024 * 1024

interface LogDropzoneProps {
  onFile: (data: LogExport) => void
}

export const LogDropzone: FC<LogDropzoneProps> = ({ onFile }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.log_viewer.dropzone'
  })
  const [isDragging, setIsDragging] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)

  const parseFile = (file: File) => {
    setParseError(null)
    if (file.size > MAX_IMPORT_BYTES) {
      setParseError(t('error_invalid'))
      return
    }
    const reader = new FileReader()
    reader.onload = e => {
      try {
        const data = parseLogExport(JSON.parse(e.target?.result as string))
        onFile(data)
      } catch (error) {
        setParseError(
          error instanceof SyntaxError ? t('error_parse') : t('error_invalid')
        )
      }
    }
    reader.onerror = () => setParseError(t('error_parse'))
    reader.readAsText(file)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) parseFile(file)
  }

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) parseFile(file)
    e.target.value = ''
  }

  return (
    <div className={styles.dropzoneArea}>
      <div
        className={cn(styles.dropzone, {
          [styles.dropzoneDragging]: isDragging
        })}
        onDrop={handleDrop}
        onDragOver={e => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
      >
        <CloudArrowDownLineIcon
          size={IconSizes.Size34}
          color='var(--grey-600)'
        />
        <p className={styles.label}>{t('label')}</p>
        <p className={styles.hint}>{t('hint')}</p>
        <label className={styles.browseButton}>
          {t('browse')}
          <input
            type='file'
            accept='.json,application/json'
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
        </label>
        {parseError && <p className={styles.parseError}>{parseError}</p>}
      </div>
    </div>
  )
}
